import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // post to php
    await fetch('email.php', {
      method: 'POST',
      headers: {
        'Content-Type': "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        service: formData.service
      })
    })

    // Mock form submission
    setTimeout(() => {
      toast({
        title: "Poruka Poslana!",
        description: "Hvala vam! Kontaktirat ćemo vas u najkraćem mogućem roku.",
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section id="contact" className="contact-section">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-[#f4e5c3]/50 rounded-full">
            <span className="text-[#b8962e] font-semibold">Kontaktirajte Nas</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-[#0f172a] mb-6 gold-underline">
            Započnimo Vaš Projekt
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Spremni smo odgovoriti na sva vaša pitanja i pružiti besplatnu procjenu vašeg projekta.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-3xl p-8 md:p-12 text-white mb-8">
              <h3 className="text-3xl font-bold mb-6">Kontakt Informacije</h3>
              <p className="text-gray-300 mb-8 leading-relaxed">
                Kontaktirajte nas putem telefona, e-maila ili posjetite naš ured.
                Tu smo da vam pomožemo ostvariti vaše građevinske snove.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#d4af37] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Telefon</p>
                    <p className="text-xl font-semibold">097 641 9996</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#d4af37] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Email</p>
                    <p className="text-xl font-semibold">dekor.art.gradnja@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#d4af37] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Adresa</p>
                    <p className="text-xl font-semibold">Davora Bašića 13</p>
                    <p className="text-lg">10290 Zaprešić, Hrvatska</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#d4af37] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Radno Vrijeme</p>
                    <p className="text-xl font-semibold">Pon - Pet: 08:00 - 16:00</p>
                    <p className="text-lg">Sub: 08:00 - 14:00</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <h4 className="text-2xl font-bold text-[#0f172a] mb-4">Područje Rada</h4>
              <p className="text-gray-600 leading-relaxed">
                Pružamo usluge u Zagrebu, Zaprešiću i okolici.
                Za projekte izvan ovih područja, kontaktirajte nas za više informacija.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <h3 className="text-3xl font-bold text-[#0f172a] mb-6">Zatražite Besplatnu Ponudu</h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Ime i Prezime *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#d4af37] focus:outline-none transition-colors"
                  placeholder="Vaše ime"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Adresa *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#d4af37] focus:outline-none transition-colors"
                  placeholder="vas-email@primjer.hr"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Broj Telefona *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#d4af37] focus:outline-none transition-colors"
                  placeholder="+385 ..."
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                  Vrsta Usluge *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#d4af37] focus:outline-none transition-colors"
                >
                  <option value="">Odaberite uslugu</option>
                  <option value="fasadni-radovi">Fasadni Radovi</option>
                  <option value="suha-gradnja">Suha Gradnja</option>
                  <option value="soboslikarski-radovi">Soboslikarski Radovi</option>
                  <option value="adaptacije">Adaptacije Ključ u Ruke</option>
                  <option value="keramika">Keramičarski Radovi</option>
                  <option value="laminat">Postave Laminata</option>
                  <option value="kocke-betonaza">Postava Kocki i Betonaža</option>
                  <option value="ostalo">Ostalo</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Poruka *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#d4af37] focus:outline-none transition-colors resize-none"
                  placeholder="Opišite vaš projekt i potrebe..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Slanje...' : 'Pošalji Upit'}
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
